<template>
  <div :class="['page-publishing']">
    <div class="page-publishing__from row bottom between">
      <InputComponent
        :label="`${$t('publishingFrom')}?`"
        data-field="from"
        :disabled="Boolean(getDisabledField('publishing.from'))"
        v-tooltip.bottom="getDisabledField('publishing.from')"
      >
        <InputDate v-model="from" :disabled="Boolean(getDisabledField('publishing.from'))" />
      </InputComponent>
      <ButtonComponent
        @click="unpublish = true"
        :disabled="Boolean(getDisabledField('publishing.to'))"
        v-tooltip.bottom="getDisabledField('publishing.to')"
        :label="$t('unpublish')"
        :icon="['fal', 'xmark']"
      />
    </div>

    <Modal :show="unpublish" @close="close" clickaway class="page-publishing__unpublish">
      <Card tag="form" variant="prompt" :title="$t('unpublishWarningTitle')" @submit.prevent="unpublishSubmit">
        <div>
          {{ $t('unpublishWarning') }}
        </div>

        <template #actions>
          <ButtonComponent :label="$t('unpublishCancel')" @click="close" />
          <ButtonComponent
            :label="$t('unpublishConfirm')"
            theme="primary"
            :icon="['fal', 'check']"
            :promise="promise"
            type="submit"
          />
        </template>
      </Card>
    </Modal>

    <InputComponent
      :class="['page-publishing__domain', { 'page-publishing__domain--show-dns': showDns }]"
      v-model.lazy="domain"
      :label="$t('domainOrWebAddress')"
      :placeholder="$t('domainPlaceholder')"
      prefix="https://"
      data-field="domain"
      :disabled="Boolean(getDisabledField('publishing.domain'))"
      v-tooltip.bottom="getDisabledField('publishing.domain')"
      :is-valid="domainProps['is-valid']"
      :validation-message="domainProps['validation-message']"
    >
      <template #after-label> <HelperPopover template="domain-helper" /> </template>
      <template #below>
        <DNS v-if="showDns" ref="dns" @update:dnsValidation="updateDomainProps" :domain="domain" class="mt-2" />
      </template>
    </InputComponent>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { InputComponent } from 'vue-elder-input'
import { AlertComponent as Alert } from 'vue-elder-alert'
import DNS from '@/components/DNS/Component.vue'
import { DateTransformer, UrlTransformer } from '@/utils/transformers'
import { ModalComponent as Modal } from 'vue-elder-modal'
import Card from '@/components/Card-v2'
import { mergeDeepRight } from 'ramda'
import InputDate from '@/components/InputDate'
import moment from 'moment'
import HelperPopover from '@/components/HelperPopover/Component'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSlot: Boolean,
    data: {
      type: Object,
      default: () => ({}),
    },
    validator: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    showDns: {
      handler(newVal) {
        if (!newVal) this.updateDomainProps(false)
      },
    },
    from(newValue, oldValue) {
      if (newValue) {
        this.to = null
      }
    },
  },
  data() {
    return {
      unpublish: false,
      promise: null,
      domainProps: {},
    }
  },
  computed: {
    ...mapState('Page', {
      item(state) {
        return state.item && Object.keys(state.item).length ? state.item : this.data
      },
    }),
    ...mapGetters('Page', { getDisabledFieldFromStore: 'getDisabledField' }),
    getDisabledField() {
      return field => {
        return this.getDisabledFieldFromStore(field, this.item)
      }
    },
    from: DateTransformer('value.from'),
    to: DateTransformer('value.to'),
    domain: UrlTransformer('value.domain'),
    redirectUrl: UrlTransformer('value.redirectUrl'),

    showDns() {
      const hasDomain = Boolean(this.domain)
      const hasValidDomain = this.validator.fields['publishing.domain'].valid

      return hasDomain && hasValidDomain
    },
  },
  methods: {
    ...mapActions('Page', ['mutate']),
    unpublishSubmit() {
      const data = {
        publishing: {
          from: null,
          to: moment().subtract(1, 'd').format('yyyy-MM-DD'),
        },
      }

      return (this.promise = this.mutate([
        {
          id: this.item.id,
          data,
          getConfig: false,
        },
        {
          transformPayload: false,
          strategy: (item, payload) => {
            const { url, previewUrl, metadata, isPublished } = payload

            const merged = mergeDeepRight(item, {
              ...data,
              url,
              previewUrl,
              isPublished,
              metadata,
            })

            if (metadata && metadata.disabledFields) {
              merged.metadata.disabledFields = metadata.disabledFields
            }

            return merged
          },
        },
      ]).then(() => {
        this.close()
      }))
    },

    close() {
      this.unpublish = false
    },
    updateDomainProps(value) {
      this.domainProps = this.getDomainProps(value)
    },
    getDomainProps(dnsIsValid) {
      if (this.validator.fields['publishing.domain'].valid === false)
        return {
          'is-valid': false,
          'validation-message': this.validator.fields['publishing.domain'].error,
        }

      if (!dnsIsValid) return { 'is-valid': undefined }
      return { 'is-valid': true }
    },
  },

  components: {
    InputComponent,
    Alert,
    DNS,
    Modal,
    Card,
    InputDate,
    HelperPopover,
  },
}
</script>

<style lang="scss">
.page-publishing {
  gap: 1rem 2rem;
  display: flex;
  flex-direction: column;
  &__from {
    gap: 1rem;
    [data-field='from'] {
      flex: 1;
    }
  }
  &__unpublish {
    .card-v2 {
      max-width: 600px;
    }
  }

  &__domain {
    &--show-dns {
      display: grid;
      grid-template-areas:
        'label label label label label'
        'input input input input dns-retry'
        'validation validation validation . .'
        'instructions instructions instructions instructions instructions';
      gap: 0.3rem 1rem;

      & > .elder-input__label {
        grid-area: label;
      }
      & > .elder-input__wrapper {
        grid-area: input;
        margin: 0;
      }
      & > .elder-input__validation-message {
        grid-area: validation;
      }
    }

    .dns-instructions {
      // for using first flex parent and display loading button beside domain input
      display: contents;

      &__retry {
        grid-area: dns-retry;
      }
      &__below {
        margin-top: 1rem;
        grid-area: instructions;
      }
    }
  }
}
</style>
